/*
 * Product add-ons
 *
 * V25 fix:
 * - Do NOT intercept the main Shopify product form submit.
 * - product.js owns the main /cart/add request and dispatches cart:item-added.
 * - This file listens for that event and adds only the checked add-ons.
 * - This prevents the main product from being added twice.
 *
 * ---------------------------------------------------------------------------
 * Three further corrections, all of them to what happens after the main
 * product has already been added to the cart. Nothing on the storefront
 * exercises this today -- no product carries the custom.product_add_ons
 * metafield -- so all three were faults waiting for the day someone fills that
 * field in on a product.
 *
 * 1. A failed add-on was invisible to the customer. The catch below logged to
 *    the console and stopped. The dress had already been added, so the cart
 *    drawer opened with the dress in it and the customer had every reason to
 *    believe the add-on was in there too. They find out when the parcel
 *    arrives. Now the failure is said out loud, next to the add-ons, and the
 *    boxes stay ticked so it can be tried again.
 *
 * 2. An add-on had nothing tying it to the piece it belongs to. It was posted
 *    as a bare variant id, so in the cart and on the order it is a separate
 *    line with no relationship to the dress. Remove the dress and the add-on
 *    stays behind, and whoever packs the order cannot tell which piece it was
 *    meant for. The parent's title and line key now travel with it as hidden
 *    line item properties (a leading underscore keeps them out of the
 *    customer's view but they are visible on the order in the admin).
 *
 *    This records the relationship; it does not enforce it. Removing the dress
 *    still leaves the add-on in the cart. Making removal cascade means acting
 *    on cart line changes, which is a change to the cart itself rather than to
 *    this file, and is better done deliberately than smuggled in here.
 *
 * 3. The same add-on could be charged twice. This listener is bound once, to
 *    the document, for the life of the page, and the checkboxes were never
 *    cleared. So: tick "add a dupatta", add to cart, then change the size and
 *    add to cart again -- the box is still ticked, the event fires again, and
 *    a second dupatta is added and charged for. The boxes are now cleared once
 *    their add-ons are safely in the cart, and a run in progress blocks a
 *    second one from starting.
 * ---------------------------------------------------------------------------
 */

(() => {
  const getSelectedAddons = () =>
    Array.from(document.querySelectorAll('.addon-checkbox:checked'))
      .filter((checkbox) => checkbox.dataset.variantId);

  const getProductQuantity = () => {
    const quantityInput = document.querySelector(
      'product-form form[data-type="add-to-cart-form"] [name="quantity"]'
    );

    const quantity = parseInt(quantityInput?.value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  /* The piece the add-ons belong to, as reported by whichever script added it.
     Shapes differ between the product form and the quick-add path, so each
     field is read defensively and simply left out if it is not there. */
  const getParentDetails = (detail) => {
    const parent = detail && detail.product ? detail.product : null;
    if (!parent) return {};

    const properties = {};
    const title = parent.product_title || parent.title;
    if (title) properties._addon_for = title;
    if (parent.key) properties._addon_for_line = parent.key;
    if (parent.id) properties._addon_for_variant = String(parent.id);
    return properties;
  };

  const showError = (message) => {
    const container = document.querySelector('.product-addons');
    if (!container) return;

    let notice = container.querySelector('.addon-error');
    if (!notice) {
      notice = document.createElement('p');
      notice.className = 'addon-error';
      notice.setAttribute('role', 'alert');
      container.appendChild(notice);
    }
    notice.textContent = message;
  };

  const clearError = () => {
    document.querySelector('.product-addons .addon-error')?.remove();
  };

  const addAddon = async (checkbox, quantity, properties) => {
    const body = {
      id: Number(checkbox.dataset.variantId),
      quantity
    };
    if (Object.keys(properties).length) {
      body.properties = properties;
    }

    const response = await fetch(theme.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`Add-on cart request failed: ${response.status}`);
    }

    return response.json();
  };

  document.addEventListener('click', (event) => {
    const card = event.target.closest('.addon-item');
    if (!card) return;

    const checkbox = card.querySelector('.addon-checkbox');
    if (!checkbox) return;

    if (event.target !== checkbox) {
      event.preventDefault();
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    }

    card.classList.toggle('selected', checkbox.checked);
  });

  document.querySelectorAll('.addon-checkbox').forEach((checkbox) => {
    checkbox.addEventListener('change', function () {
      this.closest('.addon-item')?.classList.toggle('selected', this.checked);
    });
  });

  let adding = false;

  document.addEventListener('cart:item-added', async (event) => {
    /* Two adds in quick succession would otherwise both read the same ticked
       boxes and add every add-on twice. */
    if (adding) return;

    const selectedAddons = getSelectedAddons();
    if (!selectedAddons.length) return;

    adding = true;
    clearError();

    const quantity = getProductQuantity();
    const properties = getParentDetails(event.detail);

    try {
      await Promise.all(
        selectedAddons.map((checkbox) => addAddon(checkbox, quantity, properties))
      );

      /* Cleared only once they are actually in the cart, so a failure leaves
         the selection intact and the customer can simply try again. */
      selectedAddons.forEach((checkbox) => {
        checkbox.checked = false;
        checkbox.closest('.addon-item')?.classList.remove('selected');
      });

      // Refresh cart sections/bubble after the add-ons are added.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (error) {
      console.error('[Product Add-ons]', error);
      showError(
        'Your item was added, but we could not add the extras you selected. They are still selected here — please try adding to cart again, or message us on WhatsApp and we will add them for you.'
      );
    } finally {
      adding = false;
    }
  });
})();
